import React, { useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

//Context
import { AppContext } from "@vahak/core/dist/app-context";

//Services
import { useUserActionValidationService } from "../services/useUserActionValidationService";

//Constants
import { UserAction, UserActionRequiredResponse } from "@vahak/core/dist/constants/userAction";
import { BID_ACTIONS, BiddingFormContext } from "../BiddingFormContextProvider";
import { toast } from "@vahak/core/dist/components/toast/toast";
import { UserActionValidationRequest } from "../services/useUserActionValidationService";

type ValidatedUserAction = UserAction.POST_LOAD | UserAction.POST_LORRY | UserAction.LOAD_BID | UserAction.LORRY_BID;

export type ValidateUserActionOptions = {
    companyId?: number;
    onCannotContinue?: () => void;
    onValidationError?: () => void;
    suppressCannotContinueToast?: boolean;
    suppressValidationErrorToast?: boolean;
};

const useValidateUserStatus = () => {
    const {
        isPhoneVerified,
        setShowClearDuePopup,
        setShowMandateKycPopup,
        setShowGstVerificationPopup,
        setShowUpgradeMembershipPlanPopup,
        setMandateUserVerificationCallBackFunction,
        setMandateUserVerificationActionTriggered,
        setMandateUserVerificationActionType,
        companyId,
        customerCompanyId
    } = useContext(AppContext);

    const { dispatchBid } = useContext(BiddingFormContext);

    const router = useRouter();

    const [enable, setEnable] = useState(false);
    const [validationRequest, setValidationRequest] = useState<UserActionValidationRequest | null>(null);
    const validateOptionsRef = useRef<ValidateUserActionOptions | undefined>();
    const validationCompanyIdRef = useRef(0);
    const validationCallbackRef = useRef<(() => void) | undefined>();
    const validationActiveRef = useRef(false);

    const resolveValidationCompanyId = (options?: ValidateUserActionOptions) =>
        Number(options?.companyId) || Number(customerCompanyId) || Number(companyId);

    const finishValidation = () => {
        validationActiveRef.current = false;
        validateOptionsRef.current = undefined;
        validationCallbackRef.current = undefined;
        setEnable(false);
        setValidationRequest(null);
    };

    const resetBidding = () => {
        dispatchBid({ type: BID_ACTIONS.LORRY, payload: { open: false } });
        dispatchBid({ type: BID_ACTIONS.LOAD, payload: { open: false } });
        const loadLorryId = router.query["loadbid"] || router.query["lorrybid"];
        if (loadLorryId) {
            delete router.query["lorrybid"];
            delete router.query["loadbid"];
            delete router.query["oppcmpid"];
            delete router.query["utm_medium"];
            delete router.query["utm_source"];
            delete router.query["_branch_match_id"];
            delete router.query["_branch_referrer"];
            delete router.query["r"];
            delete router.query["u_i"];
            delete router.query["utm_campaign"];
            router.replace(router, { query: "" }, { shallow: true, scroll: false });
        }
    };

    useUserActionValidationService(
        {
            onSuccess: (res) => {
                if (!validationActiveRef.current) {
                    return;
                }

                switch (res?.data.action_required) {
                    case UserActionRequiredResponse.CLEAR_CASHBACK_COINS:
                        setShowClearDuePopup?.({ isOpen: true, dueAmount: res.data.cashback_balance });
                        break;
                    case UserActionRequiredResponse.VERIFY_AADHAAR:
                        setShowMandateKycPopup?.(true);
                        break;
                    case UserActionRequiredResponse.VERIFY_PAN:
                        setShowMandateKycPopup?.(true);
                        break;
                    case UserActionRequiredResponse.VERIFY_GST:
                        setShowGstVerificationPopup?.(true);
                        break;
                    case UserActionRequiredResponse.BUY_MEMBERSHIP:
                    case UserActionRequiredResponse.UPGRADE_MEMBERSHIP:
                    case UserActionRequiredResponse.TOP_UP_MEMBERSHIP:
                    case UserActionRequiredResponse.MEMBERSHIP_EXPIRED:
                    case UserActionRequiredResponse.DAILY_MEMBERSHIP_LIMIT_EXHAUSTED:
                        setShowUpgradeMembershipPlanPopup?.(
                            true,
                            validationRequest?.action ?? UserAction.POST_LOAD,
                            res?.data.action_required
                        );
                        setMandateUserVerificationActionType?.("");
                        setMandateUserVerificationActionTriggered?.(false);
                        setMandateUserVerificationCallBackFunction?.(undefined);
                        resetBidding();
                        break;

                    default:
                        if (res?.data.can_continue) {
                            validationCallbackRef.current?.();
                        } else {
                            const options = validateOptionsRef.current;
                            if (options?.onCannotContinue) {
                                options.onCannotContinue();
                            } else if (!options?.suppressCannotContinueToast) {
                                toast.error(res?.data?.action_required);
                            }
                        }
                        break;
                }

                finishValidation();
            },
            onError: (err) => {
                if (!validationActiveRef.current) {
                    return;
                }

                console.log("Error ", err);
                const options = validateOptionsRef.current;
                if (options?.onValidationError) {
                    options.onValidationError();
                } else if (!options?.suppressValidationErrorToast) {
                    toast.error("Some thing went wrong, please try again later.");
                }

                finishValidation();
                setMandateUserVerificationActionType?.("");
                setMandateUserVerificationActionTriggered?.(false);
                setMandateUserVerificationCallBackFunction?.(undefined);
                resetBidding();
            },
            enabled: enable && Boolean(validationRequest),
            retry: 0,
            refetchOnWindowFocus: false,
            cacheTime: 0,
            staleTime: 0
        },
        validationRequest,
        {
            getCompanyId: () => validationCompanyIdRef.current || Number(customerCompanyId) || Number(companyId)
        }
    );

    const loginCheck = (
        actionType?: UserAction.POST_LOAD | UserAction.POST_LORRY | UserAction.LOAD_BID | UserAction.LORRY_BID,
        cb?: () => void
    ) => {
        if (!companyId || !isPhoneVerified) {
            router.push(
                {
                    hash: "Login"
                },
                undefined,
                { shallow: true }
            );

            if (actionType && cb) {
                setMandateUserVerificationActionType?.(actionType);
                setMandateUserVerificationActionTriggered?.(true);
                setMandateUserVerificationCallBackFunction?.(cb);
            }

            return false;
        } else return true;
    };

    const startValidation = (action: ValidatedUserAction, cb?: () => void, options?: ValidateUserActionOptions) => {
        const resolvedCompanyId = resolveValidationCompanyId(options);
        if (!resolvedCompanyId) {
            if (options?.onValidationError) {
                options.onValidationError();
            } else if (!options?.suppressValidationErrorToast) {
                toast.error("Some thing went wrong, please try again later.");
            }
            return;
        }

        validationCompanyIdRef.current = resolvedCompanyId;
        validationCallbackRef.current = cb;
        validateOptionsRef.current = options;
        validationActiveRef.current = true;
        setValidationRequest({ companyId: resolvedCompanyId, action });
        setMandateUserVerificationActionTriggered?.(true);
        setMandateUserVerificationCallBackFunction?.(cb);
        setEnable(true);
    };

    const ValidateLoadPosting = (cb?: () => void, skipLoginCheck?: boolean, options?: ValidateUserActionOptions) => {
        /**
         * Base case
         */
        if (!skipLoginCheck) {
            if (!loginCheck(UserAction.POST_LOAD, cb)) return;
        }

        startValidation(UserAction.POST_LOAD, cb, options);
    };

    const ValidateLorryPosting = (cb?: () => void, skipLoginCheck?: boolean) => {
        /**
         * Base case
         */
        if (!skipLoginCheck) {
            if (!loginCheck(UserAction.POST_LORRY, cb)) return;
        }
        //No validation for lorry posting form — still mark intentional open so
        // StandaloneLorryPostingFormModal does not strip the query on empty referrer.
        setMandateUserVerificationActionTriggered?.(true);
        return cb?.();

        /**
         * V2
         * This will refetch the c/status api
         */
        startValidation(UserAction.POST_LORRY, cb);
    };

    const ValidateLorryBidding = (cb?: () => void, skipLoginCheck?: boolean) => {
        /**
         * Base case
         */
        if (!skipLoginCheck) {
            if (!loginCheck(UserAction.LORRY_BID, cb)) return;
        }
        /**
         * V2
         * This will refetch the c/status api
         */
        startValidation(UserAction.LORRY_BID, cb);
    };

    const ValidateLoadBidding = (cb?: () => void, skipLoginCheck?: boolean, options?: ValidateUserActionOptions) => {
        /**
         * Base case
         */
        if (!skipLoginCheck) {
            if (!loginCheck(UserAction.LOAD_BID, cb)) return;
        }

        startValidation(UserAction.LOAD_BID, cb, options);
    };

    const verifyKycNow = (type: "Aadhaar" | "PAN" | "GST", cb?: () => void) => {
        /**
         * Base case
         */
        if (!loginCheck()) return;
        setMandateUserVerificationCallBackFunction?.(cb);
        if (type === "GST") {
            setShowGstVerificationPopup?.(true);
            return;
        }
        if (type === "Aadhaar" || type === "PAN") {
            setShowMandateKycPopup?.(true);
            return;
        }

        cb?.();
        setShowMandateKycPopup?.(false);
        setShowGstVerificationPopup?.(false);
        router.push({ ...router, hash: undefined });
        setMandateUserVerificationCallBackFunction?.(undefined);
    };

    return {
        ValidateLoadPosting,
        ValidateLorryPosting,
        ValidateLorryBidding,
        ValidateLoadBidding,
        verifyKycNow,
        loginCheck
    };
};

export default useValidateUserStatus;
